import { useState } from "react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@shared-kernel/primary/shared/shadcn/ui/popover";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { addMonths, format, subMonths } from "date-fns";
import { Calendar } from "@shared-kernel/primary/shared/shadcn/ui/calendar";
import { fr } from "date-fns/locale";

interface Props {
  now: Date;
  value: Date | null;
  onChange: (value: Date) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  useDefaultMonth?: boolean;
  dataTestId?: string;
  emptyMessage?: string;
}

export const PopoverCalendar = ({
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
  now,
  useDefaultMonth = false,
  dataTestId,
  emptyMessage = "Sélectionner une date",
}: Props) => {
  const [open, setOpen] = useState(false);

  // Calculate threshold dates relative to the provided now
  const MIN_THRESHOLD_DATE = subMonths(now, 48); // 4 years in the past
  const MAX_THRESHOLD_DATE = addMonths(now, 24);
  let maxDateDisabledCondition: (date: Date) => boolean = () => false;
  if (maxDate) {
    maxDateDisabledCondition = (date: Date) => date.getTime() > maxDate.getTime();
  }
  let minDateDisabledCondition: (date: Date) => boolean = () => false;
  if (minDate) {
    minDateDisabledCondition = (date: Date) => date.getTime() < minDate.getTime();
  }

  return (
    <Popover open={open} modal>
      <PopoverTrigger asChild data-testid={dataTestId}>
        <Button
          variant={"outline"}
          className={cn("w-full max-w-[240px] pl-3 text-left font-normal", !value && "text-muted-foreground")}
          onClick={() => {
            setOpen(!open);
          }}
          disabled={disabled}
        >
          {value ? format(value, "PPP", { locale: fr }) : emptyMessage}
          <CalendarIcon className="ml-auto size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} onPointerDownOutside={() => setOpen(false)}>
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={value => {
            if (value) {
              const normalized = new Date(value);
              // Use local hours (not UTC) to preserve the selected day in UTC+ timezones
              normalized.setHours(12, 0, 0, 0);
              onChange(normalized);
            }
            setOpen(false);
          }}
          disabled={date => {
            return (
              date < MIN_THRESHOLD_DATE ||
              maxDateDisabledCondition(date) ||
              minDateDisabledCondition(date) ||
              // As a safety net
              date.getTime() > MAX_THRESHOLD_DATE.getTime()
            );
          }}
          today={now}
          initialFocus
          weekStartsOn={1}
          locale={fr}
          {...(useDefaultMonth && value ? { defaultMonth: value } : {})}
        />
      </PopoverContent>
    </Popover>
  );
};
